/**
 * Centralized environment configuration with dev defaults.
 *
 * IMPORTANT: Vite requires direct access to import.meta.env.VITE_* for
 * static replacement at build time. Dynamic access like import.meta.env[key]
 * does NOT work - values won't be inlined.
 *
 * Dev defaults are applied automatically - only override in .env.local
 * for secrets or non-default values.
 */

// =============================================================================
// API & Backend
// =============================================================================

export const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'https://api.dev.cards.tapt.io';

/** Derived from API_BASE_URL */
export const SCIM_BASE_URL = `${API_BASE_URL}/api/scim/v2`;

export const STRAPI_API_URL =
  import.meta.env.VITE_STRAPI_API_URL || 'https://cms.tapt.io';

// =============================================================================
// Shopify & Shop
// =============================================================================

export const SHOP_BASE_URL =
  import.meta.env.VITE_SHOP_BASE_URL || 'https://dev-tapt.myshopify.com';

export const SHOP_DOMAIN = import.meta.env.VITE_SHOP_DOMAIN || 'tapt.io';

export const SHOP_ENV = import.meta.env.VITE_SHOP_ENV || 'dev';

export const SHOPIFY_API_ENDPOINT =
  import.meta.env.VITE_SHOPIFY_API_ENDPOINT || '';

export const SHOPIFY_STOREFRONT_API_TOKEN =
  import.meta.env.VITE_SHOPIFY_STOREFRONT_API_TOKEN || '';

// =============================================================================
// Subscriptions (Shopify variant IDs - dev defaults)
// =============================================================================

/** Derived from SHOP_BASE_URL */
export const SUBSCRIPTION_CHECKOUT_URL = `${SHOP_BASE_URL}/a/subscriptions/checkout`;

export const SUBSCRIPTION_SELLING_PLAN_ID =
  import.meta.env.VITE_SUBSCRIPTION_SELLING_PLAN_ID || '6346015010';

// Subscription tier IDs with dev defaults
export const SUBSCRIPTION_TIER_5_ID =
  import.meta.env.VITE_SUBSCRIPTION_TIER_5_ID || '42201651806282';
export const SUBSCRIPTION_TIER_10_ID =
  import.meta.env.VITE_SUBSCRIPTION_TIER_10_ID || '42201651839050';
export const SUBSCRIPTION_TIER_25_ID =
  import.meta.env.VITE_SUBSCRIPTION_TIER_25_ID || '42201651871818';
export const SUBSCRIPTION_TIER_50_ID =
  import.meta.env.VITE_SUBSCRIPTION_TIER_50_ID || '42201651904586';
export const SUBSCRIPTION_TIER_150_ID =
  import.meta.env.VITE_SUBSCRIPTION_TIER_150_ID || '42201651937354';
export const SUBSCRIPTION_TIER_250_ID =
  import.meta.env.VITE_SUBSCRIPTION_TIER_250_ID || '42201651970122';
export const SUBSCRIPTION_TIER_500_ID =
  import.meta.env.VITE_SUBSCRIPTION_TIER_500_ID || '42201652002890';
export const SUBSCRIPTION_TIER_1000_ID =
  import.meta.env.VITE_SUBSCRIPTION_TIER_1000_ID || '42201652035658';

const TIER_ID_MAP: Record<number, string> = {
  5: SUBSCRIPTION_TIER_5_ID,
  10: SUBSCRIPTION_TIER_10_ID,
  25: SUBSCRIPTION_TIER_25_ID,
  50: SUBSCRIPTION_TIER_50_ID,
  150: SUBSCRIPTION_TIER_150_ID,
  250: SUBSCRIPTION_TIER_250_ID,
  500: SUBSCRIPTION_TIER_500_ID,
  1000: SUBSCRIPTION_TIER_1000_ID,
};

export const getSubscriptionTierId = (tier: number): string =>
  TIER_ID_MAP[tier] || '';

// =============================================================================
// External Services & Integrations
// =============================================================================

export const PROFILE_LINK =
  import.meta.env.VITE_PROFILE_LINK || 'https://dev.profile.tapt.io';

export const CUSTOMISER_BASE_URL =
  import.meta.env.VITE_CUSTOMISER_BASE_URL || 'https://dev.customiser.tapt.io';

export const PARAGON_PROJECT_ID =
  import.meta.env.VITE_PARAGON_PROJECT_ID ||
  '3c9ac7e8-4ac8-454c-80bf-c8141c058271';

export const GOOGLE_ANALYTICS_MEASUREMENT_ID =
  import.meta.env.VITE_GOOGLE_ANALYTICS_MEASUREMENT_ID || '';

export const INTERCOM_WORKSPACE_ID =
  import.meta.env.VITE_INTERCOM_WORKSPACE_ID || '';

// Starshipit (prod only - empty in dev)
export const STARSHIPIT_API_KEY = import.meta.env.VITE_STARSHIPIT_API_KEY || '';
export const STARSHIPIT_SUBSCRIPTION_KEY =
  import.meta.env.VITE_STARSHIPIT_SUBSCRIPTION_KEY || '';

// =============================================================================
// Monitoring & Error Tracking
// =============================================================================

export const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN || '';
export const SENTRY_ENV = import.meta.env.VITE_SENTRY_ENV || '';

// =============================================================================
// Feature Flags
// =============================================================================

/**
 * Environment display label. Empty string hides the badge.
 * Use ?? instead of || to preserve empty string from production config.
 */
export const DISPLAY_ENV = import.meta.env.VITE_DISPLAY_ENV ?? 'local';

export const ORG_PAYMENT_TERM_AND_COLOR_SETTING =
  import.meta.env.VITE_ORG_PAYMENT_TERM_AND_COLOR_SETTING === undefined
    ? true
    : import.meta.env.VITE_ORG_PAYMENT_TERM_AND_COLOR_SETTING === 'true';

export const ALLOW_ORDER_NEW_FEATURE =
  import.meta.env.VITE_ALLOW_ORDER_NEW_FEATURE === undefined
    ? true
    : import.meta.env.VITE_ALLOW_ORDER_NEW_FEATURE === 'true';
